/**
 * Narrowing for values that came off the socket.
 *
 * Nothing the gateway sends can be taken on trust: the console is pointed at whichever backend a
 * tester chose, agents add fields between deploys, and a tool may answer with a shape no version of
 * this client has seen. So wire data enters as `unknown` and every field is asked what it is before
 * it is used. A field that is not what was expected reads as absent — `null` — rather than being
 * cast into place, because absence is a state the templates already render.
 */

/** Narrows a value to an object whose fields can be read one by one. Arrays are not objects here. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The object elements of a wire list.
 *
 * `Array.isArray` on an `unknown` widens its elements to `any`, which is how untyped wire data
 * leaks into the app; going through here is what keeps the elements narrowed.
 *
 * @param value the value a list was expected at
 * @returns its object elements, or `[]` when it was not a list
 */
export function records(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord);
}

/**
 * Narrows a field to a string with something in it.
 *
 * Returned as sent, not trimmed: these values are titles and synopses that a template lays out, and
 * only the emptiness check cares about surrounding space.
 *
 * @param value the field
 * @returns the string, or `null` when absent, not a string, or blank
 */
export function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Narrows a field to a usable number, rejecting `NaN` and the infinities. */
export function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Narrows a field to a boolean. Truthy strings and 0/1 are not booleans. */
export function bool(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/**
 * Narrows a field to a list of non-blank strings.
 *
 * @param value the field
 * @returns the strings, or `null` when there are none — an empty list of genres is no genres
 */
export function textList(value: unknown): string[] | null {
  const usable = texts(value);
  return usable.length ? usable : null;
}

/** Narrows a field to a list of non-blank strings, where having none is not worth distinguishing. */
export function texts(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => text(item) !== null);
}
